import React from "react";
import { Chip, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NewReleasesTwoToneIcon from "@mui/icons-material/NewReleasesTwoTone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneIcon from "@mui/icons-material/Done";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { ColumnStyles } from "./columnStyles";

const DELETED = 141;
const ABORTED_NO_VALIDATED = 140;
const FINISHED_WITH_ERRORS = 139;
const FINISHED_INCOMPLETE = 138;
const FINISHED_COMPLETE = 137;
const IN_PROGRESS = 136;

interface ComputedStatusChipProps {
  statusId: number | undefined | null;
}

const ComputedStatusChip: React.FC<ComputedStatusChipProps> = ({
  statusId,
}) => {
  const [t] = useTranslation("global");

  const statusConfig: Record<
    number,
    {
      chipColor: "default" | "error" | "info" | "success";
      label: string;
      styles: { backgroundColor: string; color: string; borderColor: string };
      icon: React.ReactNode;
    }
  > = {
    [DELETED]: {
      chipColor: "default",
      label: t("Data.table.manage_status_columns.141"),
      styles: ColumnStyles.statusChips.default,
      icon: (
        <DeleteOutlineIcon
          sx={{ ...ColumnStyles.statusIcon, color: "#9e9e9e" }}
        />
      ),
    },
    [ABORTED_NO_VALIDATED]: {
      chipColor: "default",
      label: t("Data.table.manage_status_columns.140"),
      styles: ColumnStyles.statusChips.default,
      icon: (
        <NewReleasesTwoToneIcon
          sx={{ ...ColumnStyles.statusIcon, color: "#9e9e9e" }}
        />
      ),
    },
    [FINISHED_WITH_ERRORS]: {
      chipColor: "error",
      label: t("Data.table.manage_status_columns.139"),
      styles: ColumnStyles.statusChips.failed,
      icon: <DoneIcon sx={{ ...ColumnStyles.statusIcon, color: "#d32f2f" }} />,
    },
    [FINISHED_INCOMPLETE]: {
      chipColor: "info",
      label: t("Data.table.manage_status_columns.138"),
      styles: ColumnStyles.statusChips.processing,
      icon: <DoneIcon sx={{ ...ColumnStyles.statusIcon, color: "#1976d2" }} />,
    },
    [FINISHED_COMPLETE]: {
      chipColor: "success",
      label: t("Data.table.manage_status_columns.137"),
      styles: ColumnStyles.statusChips.completed,
      icon: (
        <CheckCircleOutlineIcon
          sx={{ ...ColumnStyles.statusIcon, color: "#2e7d32" }}
        />
      ),
    },
    [IN_PROGRESS]: {
      chipColor: "info",
      label: t("Data.table.manage_status_columns.136"),
      styles: ColumnStyles.statusChips.processing,
      icon: (
        <FontAwesomeIcon
          icon={faClockRotateLeft}
          fontSize="14px"
          color="#1976d2"
          style={{ marginRight: "4px" }}
        />
      ),
    },
  };

  const defaultConfig = {
    chipColor: "default" as const,
    label: t("Data.table.status_columns.default"),
    styles: ColumnStyles.statusChips.default,
    icon: <HelpOutlineIcon sx={ColumnStyles.statusIcon} />,
  };

  const config =
    statusId != null && statusConfig[statusId]
      ? statusConfig[statusId]
      : defaultConfig;

  const tooltipDescription = () => {
    switch (statusId) {
      case DELETED:
        return t("Data.table.manage_status_columns_desc.141");
      case ABORTED_NO_VALIDATED:
        return t("Data.table.manage_status_columns_desc.140");
      case FINISHED_WITH_ERRORS:
        return t("Data.table.manage_status_columns_desc.139");
      case FINISHED_INCOMPLETE:
        return t("Data.table.manage_status_columns_desc.138");
      case FINISHED_COMPLETE:
        return t("Data.table.manage_status_columns_desc.137");
      case IN_PROGRESS:
        return (
          <span>
            {` ${t("Data.table.manage_status_columns_desc.136")}  `}
            <span style={{ color: "#ffd54f", fontWeight: 600 }}>
              {t("Data.table.manage_status_columns_desc.136desc")}
            </span>
          </span>
        );
      default:
        return t("Data.table.status_columns_desc.default");
    }
  };

  return (
    <Tooltip title={tooltipDescription()} arrow>
      <Chip
        label={
          <div style={ColumnStyles.chipContainer}>
            {config.icon}
            {config.label}
          </div>
        }
        color={config.chipColor}
        size="small"
        variant="outlined"
        sx={{
          ...ColumnStyles.statusChip,
          backgroundColor: config.styles.backgroundColor,
          color: config.styles.color,
          borderColor: config.styles.borderColor,
        }}
      />
    </Tooltip>
  );
};

export default ComputedStatusChip;
